/*  API client, talks to the live Flask + SQLite backend.
    Auth is password-only via a token stored in localStorage and sent as the
    X-Session-Token header on every request.
*/
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API  = BASE.replace(/\/+$/, "") + "/api";

const TOKEN_KEY = "htcc_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
const setToken = (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); };

const client = axios.create({ baseURL: API });
client.interceptors.request.use((cfg) => {
    const t = getToken();
    if (t) cfg.headers["X-Session-Token"] = t;
    return cfg;
});

// ---------------- Auth ----------------
export const passwordLogin = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    setToken(data.token);
    return data;
};
export const fetchMe = async () => (await client.get("/auth/me")).data;
// Legacy no-op, Google session exchange no longer supported; the admin login
// page now uses passwordLogin(). Kept as an export so AuthCallback still compiles.
export const exchangeSession = async () => { throw new Error("Google Sign-In is no longer used, please sign in with your email and password."); };
export const logout  = async () => {
    try { await client.post("/auth/logout"); } finally { setToken(""); }
    return { ok: true };
};

// ---------------- Public ----------------
export const fetchSiteContent = async () => (await client.get("/content")).data;

export const submitPrayerIntention = async (payload) => (await client.post("/prayers", payload)).data;
export const fetchPrayerIntentions = async () => (await client.get("/prayers")).data;
export const prayForIntention      = async (id) => {
    try { return (await client.post(`/prayers/${id}/pray`)).data; }
    catch (e) { return { ok: true }; }
};

export const submitGroupJoin = async (payload) => (await client.post("/join", payload)).data;

export const fetchBlogs   = async () => (await client.get("/blogs")).data;
export const fetchBlog    = async (id) => (await client.get(`/blogs/${id}`)).data;

export const fetchPhotos  = async () => (await client.get("/photos")).data;
export const photoUrl     = (p) => (BASE.replace(/\/+$/,"") + p.url);

// ---------------- Admin ----------------
export const adminListIntentions  = async () => (await client.get("/admin/prayers")).data;
export const adminApproveIntention= async (id, approved = true) => (await client.put(`/admin/prayers/${id}`, { approved })).data;
export const adminDeleteIntention = async (id) => (await client.delete(`/admin/prayers/${id}`)).data;

export const adminListGroupJoins  = async () => (await client.get("/admin/joins")).data;
export const adminDeleteGroupJoin = async (id) => (await client.delete(`/admin/joins/${id}`)).data;
export const adminGroupJoinsCsvUrl = () => `${API}/admin/joins?token=${encodeURIComponent(getToken())}&format=csv`;

export const adminListBlogs   = async () => (await client.get("/admin/blogs")).data;
export const adminCreateBlog  = async (payload) => (await client.post("/admin/blogs", payload)).data;
export const adminUpdateBlog  = async (id, payload) => (await client.put(`/admin/blogs/${id}`, payload)).data;
export const adminApproveBlog = async (id, published = true) => adminUpdateBlog(id, { published });
export const adminDeleteBlog  = async (id) => (await client.delete(`/admin/blogs/${id}`)).data;

export const adminUploadPhoto = async (file, caption = "") => {
    const fd = new FormData();
    fd.append("file", file);
    if (caption) fd.append("caption", caption);
    const { data } = await client.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data;
};
export const adminDeletePhoto = async (id) => (await client.delete(`/admin/photos/${id}`)).data;
export const adminUpdatePhotoCaption = async (id, caption) => (await client.patch(`/admin/photos/${id}`, { caption })).data;

export const adminSaveSiteContent = async (payload) => (await client.put("/admin/content", payload)).data;

// ---------------- Groups ----------------
export const fetchGroups          = async () => (await client.get("/groups")).data;
export const fetchGroup           = async (slug) => (await client.get(`/groups/${slug}`)).data;
export const adminUpdateGroup     = async (id, payload) => (await client.put(`/admin/groups/${id}`, payload)).data;
export const adminUploadGroupImage = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await client.post(`/admin/groups/${id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const adminUploadGroupMemberPhoto = async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await client.post(`/admin/groups/${id}/member-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const adminDeleteGroupMemberPhoto = async (id) => (await client.delete(`/admin/groups/${id}/member-photo`)).data;

// ---------------- Events ----------------
export const fetchEvents           = async () => (await client.get("/events")).data;
export const adminCreateEvent      = async (formData) =>
    (await client.post("/admin/events", formData, { headers: { "Content-Type": "multipart/form-data" } })).data;
export const adminUpdateEvent      = async (id, payload) => (await client.put(`/admin/events/${id}`, payload)).data;
export const adminDeleteEvent      = async (id) => (await client.delete(`/admin/events/${id}`)).data;

// ---------------- Admin account ----------------
export const adminChangePassword = async (current, next) =>
    (await client.post("/auth/change-password", { current, new: next })).data;

// ---------------- Announcements ----------------
export const fetchAnnouncements     = async (limit = 20) => (await client.get(`/announcements?limit=${limit}`)).data;
export const adminCreateAnnouncement = async (formData) =>
    (await client.post("/admin/announcements", formData, { headers: { "Content-Type": "multipart/form-data" } })).data;
export const adminDeleteAnnouncement = async (id) => (await client.delete(`/admin/announcements/${id}`)).data;

// ---------------- Slides ----------------
export const fetchSlides       = async (context = "home") => (await client.get(`/slides?context=${encodeURIComponent(context)}`)).data;
export const adminUploadSlide  = async (file, caption = "", context = "home") => {
    const fd = new FormData();
    fd.append("file", file);
    if (caption) fd.append("caption", caption);
    fd.append("context", context);
    return (await client.post("/admin/slides", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const adminDeleteSlide  = async (id) => (await client.delete(`/admin/slides/${id}`)).data;
export const adminReorderSlides = async (orderIds) =>
    (await client.put("/admin/slides/reorder", { order: orderIds })).data;

// ---------------- Settings / Posters ----------------
export const fetchSetting        = async (key) => (await client.get(`/settings/${key}`)).data;
export const adminUploadPoster   = async (key, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await client.post(`/admin/posters/${key}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const adminUploadCalendarPdf = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return (await client.post(`/admin/posters/calendar_pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};

// ---------------- Prayer approvals ----------------
export const adminApprovePrayer = async (id, approved = true) =>
    (await client.put(`/admin/prayers/${id}`, { approved })).data;

// ---------------- Bulletins ----------------
export const fetchBulletins       = async () => (await client.get("/bulletins")).data;
export const adminUploadBulletin  = async (file, { title, year }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title);
    fd.append("year", String(year));
    return (await client.post("/admin/bulletins", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
};
export const adminDeleteBulletin  = async (id) => (await client.delete(`/admin/bulletins/${id}`)).data;

// ---------------- Bulletin subscribers ----------------
export const subscribeToBulletin = async (email, name = "") =>
    (await client.post("/subscribe", { email, name })).data;

export const adminListSubscribers = async () => (await client.get("/admin/subscribers")).data;
export const adminDeleteSubscriber = async (id) => (await client.delete(`/admin/subscribers/${id}`)).data;
export const adminSubscribersCsvUrl = () =>
    `${API}/admin/subscribers?token=${encodeURIComponent(getToken())}&format=csv`;

// ------- Legacy aliases (kept so all existing pages compile unchanged) -------
export const createBlog       = async (payload) => (await client.post("/blogs", payload)).data;
export const updateBlog       = adminUpdateBlog;
export const deleteBlog       = adminDeleteBlog;
export const listBlogs        = fetchBlogs;
export const getBlog          = fetchBlog;
export const submitIntention  = submitPrayerIntention;
export const listIntentions   = fetchPrayerIntentions;
export const submitJoinRequest= submitGroupJoin;
export const listPhotos       = fetchPhotos;
export const uploadPhoto      = adminUploadPhoto;
export const deletePhoto      = adminDeletePhoto;
export const fetchIntentions  = fetchPrayerIntentions;
export const createIntention  = submitPrayerIntention;

// Convenience: also export the base URL so pages can build image src for admin uploads
export const API_BASE = BASE.replace(/\/+$/,"");

// ---------------- Daily verse & saint (client-side pool) ----------------
// The Flask backend intentionally has no /daily endpoint. We keep the same
// UI experience by cycling a curated pool by the day-of-year.
const DAILY_POOL = {
    verses: [
        { ref: "John 3:16",       text: "For God so loved the world that He gave His only Son, that whoever believes in Him should not perish but have eternal life." },
        { ref: "Philippians 4:13", text: "I can do all things through Him who strengthens me." },
        { ref: "Psalm 23:1",       text: "The Lord is my shepherd; there is nothing I lack." },
        { ref: "Matthew 5:16",     text: "Let your light shine before others, so that they may see your good deeds and glorify your heavenly Father." },
        { ref: "Isaiah 41:10",     text: "Do not fear, for I am with you; do not be anxious, for I am your God." },
        { ref: "Romans 8:28",      text: "We know that all things work for good for those who love God." },
        { ref: "1 Cor. 13:4-5",    text: "Love is patient, love is kind; it is not jealous, is not pompous, is not inflated, is not rude." },
        { ref: "Jeremiah 29:11",   text: "For I know well the plans I have in mind for you, plans for your welfare and not for woe." },
        { ref: "Matthew 11:28",    text: "Come to me, all you who labour and are burdened, and I will give you rest." },
        { ref: "Psalm 46:10",      text: "Be still and know that I am God." },
        { ref: "James 1:12",       text: "Blessed is the one who perseveres in trial." },
        { ref: "Luke 1:46-47",     text: "My soul proclaims the greatness of the Lord; my spirit rejoices in God my Saviour." },
    ],
    saints: [
        { name: "St. Joseph",         feast: "March 19",    blurb: "Foster-father of Jesus and universal patron of the Church, model of humble faith and quiet obedience." },
        { name: "Our Lady of Fatima", feast: "May 13",      blurb: "Mother of God, who appeared to three shepherd children calling the world to prayer, penance and the Rosary." },
        { name: "St. Padre Pio",      feast: "September 23",blurb: "Capuchin priest, confessor and stigmatist, renowned for his tireless ministry of the confessional." },
        { name: "St. Faustina Kowalska", feast: "October 5",blurb: "Apostle of Divine Mercy, 'Jesus, I trust in You.'" },
        { name: "St. Therese of the Child Jesus", feast: "October 1", blurb: "Doctor of the Church, whose 'little way' of trust and love opened the road to holiness for all." },
        { name: "St. Francis of Assisi", feast: "October 4",blurb: "Deacon and founder of the Franciscans; herald of Gospel poverty, joy and care for creation." },
        { name: "St. Monica",         feast: "August 27",   blurb: "Mother of St. Augustine, model of prayerful perseverance for her family." },
        { name: "St. John of God",    feast: "March 8",     blurb: "Founder of the Hospitaller Order; patron of the sick, nurses and hospitals." },
        { name: "St. Lawrence",       feast: "August 10",   blurb: "Deacon and martyr of Rome, who called the poor 'the true treasures of the Church.'" },
        { name: "St. Alphonsus de Liguori", feast: "August 1", blurb: "Bishop, Doctor of the Church and founder of the Redemptorists, theologian of Christian moral life." },
        { name: "St. Joseph Allamano", feast: "February 16",blurb: "Founder of the Consolata Missionaries; sent missionaries to Kenya from the very beginnings of the Kenyan Church." },
        { name: "Blessed Sister Irene", feast: "September 1", blurb: "Sister of Charity and pioneer in caring for orphans and immigrant children, model of compassionate service." },
    ],
};
const _dayIndex = (arr) => {
    const now = new Date();
    const start = Date.UTC(now.getUTCFullYear(), 0, 0);
    const doy = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - start) / 86400000);
    return arr[doy % arr.length];
};
export const fetchDaily = async () => ({
    verse: _dayIndex(DAILY_POOL.verses),
    saint: _dayIndex(DAILY_POOL.saints),
});

export default client;
